import { useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bell,
  BellRing,
  Package,
  Truck,
  CheckCircle,
  MessageSquare,
  DollarSign,
  ThumbsUp,
  ThumbsDown,
  Gavel,
  AlertTriangle,
  Trophy,
  Clock,
  Star,
  Tag,
  Megaphone,
  CheckCheck,
} from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { useAuthStore } from '@/store/authStore'
import { useNotificationStore } from '@/store/notificationStore'
import { usePushNotifications } from '@/hooks/usePushNotifications'
import type { NotificationType, Notification } from '@/types/notification'

/** Map notification type to an icon component and colour */
function getNotificationIcon(type: NotificationType) {
  const map: Record<NotificationType, { icon: React.ReactNode; color: string }> = {
    order_paid: { icon: <Package className="w-5 h-5" />, color: 'text-green-600 bg-green-100' },
    order_shipped: { icon: <Truck className="w-5 h-5" />, color: 'text-blue-600 bg-blue-100' },
    order_completed: { icon: <CheckCircle className="w-5 h-5" />, color: 'text-green-600 bg-green-100' },
    new_message: { icon: <MessageSquare className="w-5 h-5" />, color: 'text-primary-600 bg-primary-100' },
    new_offer: { icon: <DollarSign className="w-5 h-5" />, color: 'text-orange-600 bg-orange-100' },
    offer_accepted: { icon: <ThumbsUp className="w-5 h-5" />, color: 'text-green-600 bg-green-100' },
    offer_declined: { icon: <ThumbsDown className="w-5 h-5" />, color: 'text-red-600 bg-red-100' },
    new_bid: { icon: <Gavel className="w-5 h-5" />, color: 'text-purple-600 bg-purple-100' },
    outbid: { icon: <AlertTriangle className="w-5 h-5" />, color: 'text-amber-600 bg-amber-100' },
    auction_won: { icon: <Trophy className="w-5 h-5" />, color: 'text-yellow-600 bg-yellow-100' },
    auction_ended: { icon: <Clock className="w-5 h-5" />, color: 'text-slate-600 bg-slate-100' },
    new_review: { icon: <Star className="w-5 h-5" />, color: 'text-yellow-600 bg-yellow-100' },
    listing_sold: { icon: <Tag className="w-5 h-5" />, color: 'text-green-600 bg-green-100' },
    system: { icon: <Megaphone className="w-5 h-5" />, color: 'text-blue-600 bg-blue-100' },
  }
  return map[type] ?? map.system
}

function formatTimestamp(notification: Notification): string {
  if (!notification.createdAt?.toDate) return ''
  try {
    return formatDistanceToNow(notification.createdAt.toDate(), { addSuffix: true })
  } catch {
    return ''
  }
}

export function NotificationBell() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const {
    notifications,
    unreadCount,
    isOpen,
    toggle,
    close,
    markAsRead,
    markAllAsRead,
  } = useNotificationStore()
  const { permissionStatus, isSupported, requestPermission } = usePushNotifications()

  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        close()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [close])

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification.id)
    }
    close()
    if (notification.link) {
      navigate(notification.link)
    }
  }

  const handleMarkAllAsRead = () => {
    if (user) {
      markAllAsRead(user.uid)
    }
  }

  const handleViewAll = () => {
    close()
    navigate('/notifications')
  }

  // Show at most 10 in the dropdown
  const visibleNotifications = notifications.slice(0, 10)

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={toggle}
        className="btn-ghost p-2 relative"
        aria-label="Notifications"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold min-w-[20px] h-5 rounded-full flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 max-w-[calc(100vw-1rem)] bg-white rounded-lg shadow-lg border border-slate-200 animate-fade-in z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="font-semibold text-slate-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Mark all read
              </button>
            )}
          </div>

          {/* Push notification prompt */}
          {isSupported && permissionStatus === 'default' && (
            <button
              onClick={requestPermission}
              className="w-full flex items-center gap-2 px-4 py-2.5 bg-primary-50 text-primary-700 text-sm hover:bg-primary-100 transition-colors border-b border-primary-100"
            >
              <BellRing className="w-4 h-4 flex-shrink-0" />
              <span>Enable push notifications</span>
            </button>
          )}

          {/* Notification list */}
          <div className="max-h-[400px] overflow-y-auto">
            {visibleNotifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No notifications yet</p>
              </div>
            ) : (
              visibleNotifications.map((notification) => {
                const { icon, color } = getNotificationIcon(notification.type)
                return (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={clsx(
                      'w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-b-0',
                      !notification.read && 'bg-primary-50/40'
                    )}
                  >
                    <div className={clsx('flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center', color)}>
                      {icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={clsx(
                        'text-sm leading-snug',
                        notification.read ? 'text-slate-700' : 'text-slate-900 font-medium'
                      )}>
                        {notification.title}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {formatTimestamp(notification)}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="flex-shrink-0 w-2 h-2 rounded-full bg-primary-500 mt-2" />
                    )}
                  </button>
                )
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="border-t border-slate-100">
              <button
                onClick={handleViewAll}
                className="w-full py-3 text-sm text-primary-600 hover:text-primary-700 font-medium hover:bg-slate-50 transition-colors"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
